import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { Button } from '@/components/ui/button';
import { Lock, Crown, Sparkles } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * FeatureGate - Wraps components that require specific subscription features
 * 
 * @param {string} feature - 'time_tracking' | 'ai_features' | 'advanced_reports'
 * @param {string} requiredPlan - 'pro' | 'business' (for display purposes)
 * @param {React.ReactNode} children - The component to render if access is granted
 * @param {string} fallback - 'disable' | 'hide' | 'dialog' - what to do if no access
 */
export function FeatureGate({ 
  feature, 
  requiredPlan = 'pro',
  children, 
  fallback = 'disable',
  tooltipText,
  className = ''
}) {
  const { features, canUseTimeTracking, canUseAIFeatures, canUseAdvancedReports } = useSubscription();
  const navigate = useNavigate();
  const [showUpgradeDialog, setShowUpgradeDialog] = React.useState(false);

  // Check if user has access to the feature
  const hasAccess = () => {
    switch (feature) {
      case 'time_tracking':
        return canUseTimeTracking();
      case 'ai_features':
        return canUseAIFeatures();
      case 'advanced_reports':
        return canUseAdvancedReports();
      default:
        return true;
    }
  };

  const getFeatureInfo = () => {
    switch (feature) {
      case 'time_tracking':
        return {
          name: 'Zeiterfassung',
          description: 'Erfassen Sie Arbeitszeiten für Tasks und Projekte.',
          requiredPlan: 'Pro',
          icon: <Lock className="h-5 w-5" />
        };
      case 'ai_features':
        return {
          name: 'KI-Features',
          description: 'Nutzen Sie KI für Reports und Projektvorlagen.',
          requiredPlan: 'Business',
          icon: <Sparkles className="h-5 w-5" />
        };
      case 'advanced_reports':
        return {
          name: 'Erweiterte Reports',
          description: 'Detaillierte Analysen und Exportfunktionen.',
          requiredPlan: 'Business',
          icon: <Crown className="h-5 w-5" />
        };
      default:
        return {
          name: 'Feature',
          description: 'Diese Funktion erfordert ein Upgrade.',
          requiredPlan: 'Pro',
          icon: <Lock className="h-5 w-5" />
        };
    }
  };

  // If user has access, render children normally
  if (hasAccess()) {
    return <>{children}</>;
  }

  const featureInfo = getFeatureInfo();
  const defaultTooltip = `${featureInfo.name} erfordert ${featureInfo.requiredPlan}-Abo`;

  // Handle different fallback behaviors
  if (fallback === 'hide') {
    return null;
  }

  if (fallback === 'dialog') {
    return (
      <>
        <div 
          className={`relative cursor-pointer ${className}`}
          onClick={() => setShowUpgradeDialog(true)}
        >
          <div className="opacity-60 pointer-events-none">
            {children}
          </div>
          <div className="absolute inset-0 flex items-center justify-center bg-background/50 rounded">
            <Lock className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>

        <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {featureInfo.icon}
                {featureInfo.name} freischalten
              </DialogTitle>
              <DialogDescription>
                {featureInfo.description}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-muted-foreground">
                Diese Funktion ist im <strong>{featureInfo.requiredPlan}</strong>-Plan oder höher verfügbar.
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                Ihr aktueller Plan: <strong className="text-foreground">{features.plan.toUpperCase()}</strong>
                {features.is_trial && ' (Trial)'}
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUpgradeDialog(false)}>
                Abbrechen
              </Button>
              <Button onClick={() => navigate('/subscription')}>
                <Crown className="h-4 w-4 mr-2" />
                Upgrade
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Default: disable with tooltip
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={`relative ${className}`}>
            <div className="opacity-50 pointer-events-none">
              {children}
            </div>
            <div className="absolute inset-0 flex items-center justify-center cursor-not-allowed">
              <Lock className="h-3 w-3 text-muted-foreground" />
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltipText || defaultTooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * UpgradeButton - A button that shows upgrade prompt for locked features
 */
export function UpgradeButton({ 
  feature, 
  children, 
  variant = 'outline',
  size = 'default',
  className = '',
  onClick,
  ...props 
}) {
  const navigate = useNavigate();
  const { canUseTimeTracking, canUseAIFeatures, canUseAdvancedReports, features } = useSubscription();
  const [showDialog, setShowDialog] = React.useState(false);

  const hasAccess = () => {
    switch (feature) {
      case 'time_tracking':
        return canUseTimeTracking();
      case 'ai_features':
        return canUseAIFeatures();
      case 'advanced_reports':
        return canUseAdvancedReports();
      default:
        return true;
    }
  };

  const getRequiredPlan = () => {
    switch (feature) {
      case 'time_tracking':
        return 'Pro';
      case 'ai_features':
      case 'advanced_reports':
        return 'Business';
      default:
        return 'Pro';
    }
  };

  const handleClick = (e) => {
    if (hasAccess()) {
      onClick?.(e);
    } else {
      e.preventDefault();
      setShowDialog(true);
    }
  };

  const requiredPlan = getRequiredPlan();
  const isLocked = !hasAccess();

  return (
    <>
      <Button 
        variant={variant} 
        size={size}
        className={`${className} ${isLocked ? 'relative' : ''}`}
        onClick={handleClick}
        {...props}
      >
        {children}
        {isLocked && (
          <Lock className="h-3 w-3 ml-1 text-muted-foreground" />
        )}
      </Button>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-yellow-500" />
              Upgrade erforderlich
            </DialogTitle>
            <DialogDescription>
              Diese Funktion erfordert ein {requiredPlan}-Abonnement.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Ihr aktueller Plan: <strong className="text-foreground">{features.plan.toUpperCase()}</strong>
              {features.is_trial && ' (Trial)'}
            </p>
            <p className="text-sm mt-2">
              Upgraden Sie auf <strong>{requiredPlan}</strong>, um diese und weitere Features freizuschalten.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Später
            </Button>
            <Button onClick={() => navigate('/subscription')}>
              <Crown className="h-4 w-4 mr-2" />
              Jetzt upgraden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default FeatureGate;
